import { useCallback, useEffect, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";

type Kind = "light-logo" | "dark-logo" | "favicon";

interface BrandingSummary {
  hasLightLogo: boolean;
  lightLogoMime: string | null;
  hasDarkLogo: boolean;
  darkLogoMime: string | null;
  hasFavicon: boolean;
  faviconMime: string | null;
  updatedAt: string | null;
}

interface BrandingResponse {
  branding: BrandingSummary;
  slug: string;
}

const KIND_DETAILS: Record<
  Kind,
  { label: string; description: string; accept: string; bgClass: string }
> = {
  "light-logo": {
    label: "Light-mode logo",
    description: "Shown in the header and sidebar when the user has light mode active.",
    accept: "image/svg+xml,image/png,image/jpeg",
    bgClass: "bg-white",
  },
  "dark-logo": {
    label: "Dark-mode logo",
    description: "Shown in the header and sidebar when the user has dark mode active.",
    accept: "image/svg+xml,image/png,image/jpeg",
    bgClass: "bg-slate-900",
  },
  favicon: {
    label: "Favicon",
    description:
      "Shown in the browser tab. SVG preferred for sharpness across resolutions; PNG and ICO accepted.",
    accept: "image/svg+xml,image/png,image/x-icon,image/vnd.microsoft.icon",
    bgClass: "bg-slate-100 dark:bg-slate-800",
  },
};

const KINDS: Kind[] = ["light-logo", "dark-logo", "favicon"];

export function SettingsBranding() {
  const { slug } = useParams<{ slug: string }>();
  const [summary, setSummary] = useState<BrandingSummary | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [busyKind, setBusyKind] = useState<Kind | null>(null);
  // Cache-buster appended to the preview <img> URLs after an upload so the
  // browser fetches the new bytes instead of the previous (still-cacheable)
  // response.
  const [version, setVersion] = useState<number>(() => Date.now());

  const refresh = useCallback(async () => {
    try {
      const res = await api<BrandingResponse>("/organisations/me/branding");
      setSummary(res.branding);
    } catch (err) {
      setErrors(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const upload = async (kind: Kind, file: File) => {
    // Validate kind/MIME pairing client-side too so drag-drop of the wrong
    // type fails fast with a clear message rather than a server 400.
    const accept = KIND_DETAILS[kind].accept.split(",");
    if (!accept.includes(file.type)) {
      setErrors([
        `${KIND_DETAILS[kind].label}: file type '${file.type || "unknown"}' is not allowed. Permitted: ${accept.join(", ")}.`,
      ]);
      return;
    }
    setBusyKind(kind);
    setErrors([]);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api(`/organisations/me/branding/${kind}`, {
        method: "PUT",
        body: formData,
      });
      setVersion(Date.now());
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusyKind(null);
    }
  };

  const remove = async (kind: Kind) => {
    setBusyKind(kind);
    setErrors([]);
    try {
      await api(`/organisations/me/branding/${kind}`, { method: "DELETE" });
      setVersion(Date.now());
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusyKind(null);
    }
  };

  const isPresent = (kind: Kind, s: BrandingSummary): boolean =>
    kind === "light-logo" ? s.hasLightLogo : kind === "dark-logo" ? s.hasDarkLogo : s.hasFavicon;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/o/${slug}/settings`}
          className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
        >
          ← Settings
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">Branding</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Upload your organisation&rsquo;s logos and favicon. Logos appear in the header and sidebar
          for users inside your organisation; the favicon shows in the browser tab. Files are
          limited to 1 MB each.
        </p>
      </div>

      {errors.length > 0 ? (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {errors[0]}
        </div>
      ) : null}

      {summary === null ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      ) : (
        <ul className="grid gap-4 md:grid-cols-3">
          {KINDS.map((kind) => (
            <BrandingCard
              key={kind}
              kind={kind}
              isPresent={isPresent(kind, summary)}
              busy={busyKind === kind}
              slug={slug ?? ""}
              version={version}
              onUpload={(file) => void upload(kind, file)}
              onRemove={() => void remove(kind)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function BrandingCard({
  kind,
  isPresent,
  busy,
  slug,
  version,
  onUpload,
  onRemove,
}: {
  kind: Kind;
  isPresent: boolean;
  busy: boolean;
  slug: string;
  version: number;
  onUpload: (file: File) => void;
  onRemove: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const details = KIND_DETAILS[kind];
  const base = import.meta.env.VITE_API_URL ?? "/api/v1";
  const previewUrl = isPresent ? `${base}/branding/${slug}/${kind}?v=${version}` : null;

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!busy) setIsDragOver(true);
  };

  const onDragLeave = () => setIsDragOver(false);

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (busy) return;
    const file = e.dataTransfer.files?.[0];
    if (file) onUpload(file);
  };

  return (
    <li className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{details.label}</h2>
      <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">{details.description}</p>

      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        aria-label={`Drop a ${details.label.toLowerCase()} here, or use the Upload button`}
        className={`mt-3 flex h-32 items-center justify-center rounded-md border-2 border-dashed transition ${
          isDragOver
            ? "border-brand-500 bg-brand-50 dark:bg-brand-500/10"
            : "border-slate-200 dark:border-slate-700"
        } ${details.bgClass}`}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={`${details.label} preview`}
            className={
              kind === "favicon" ? "h-12 w-12 object-contain" : "max-h-24 max-w-full object-contain"
            }
          />
        ) : (
          <span className="text-xs text-slate-400 dark:text-slate-500">
            {isDragOver ? "Drop to upload" : "Not set — drop a file here or use Upload"}
          </span>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <label className="inline-flex cursor-pointer items-center rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 focus-within:ring-2 focus-within:ring-brand-500">
          {busy ? "Uploading…" : isPresent ? "Replace" : "Upload"}
          <input
            ref={inputRef}
            type="file"
            accept={details.accept}
            onChange={onPick}
            disabled={busy}
            className="hidden"
          />
        </label>
        {isPresent ? (
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60 dark:border-red-800 dark:bg-slate-800 dark:text-red-200 dark:hover:bg-red-950/40"
          >
            Remove
          </button>
        ) : null}
      </div>
    </li>
  );
}
